import React from "react";
import { motion } from "framer-motion";
import { Star, ExternalLink, Quote } from "lucide-react";
import { reviewsData } from "../data/mock";
import { Button } from "./ui/button";

const ReviewsSection = () => {
  return (
    <section className="py-24 bg-slate-50 relative overflow-hidden">
      {/* 🔹 Decorative Background Elements */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-1 bg-gradient-to-r from-transparent via-amber-500/20 to-transparent" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header Section */}
        <div className="text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            className="flex justify-center items-center gap-1 mb-4"
          >
            {[1, 2, 3, 4, 5].map((i) => (
              <Star
                key={i}
                className="w-6 h-6 fill-amber-500 text-amber-500 drop-shadow-sm"
              />
            ))}
          </motion.div>

          <h2 className="text-4xl md:text-6xl font-black text-slate-900 tracking-tighter mb-4">
            Trusted by{" "}
            <span className="text-amber-600 italic">
              {reviewsData.totalReviews}+
            </span>{" "}
            Travelers
          </h2>

          <p className="text-slate-500 text-lg md:text-xl font-medium max-w-2xl mx-auto">
            Experience Udaipur with the highest-rated cab service on{" "}
            <span className="text-slate-900 font-bold underline decoration-amber-500 decoration-4 underline-offset-4">
              {reviewsData.source}
            </span>
            .
          </p>
        </div>

        {/* Reviews Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {reviewsData.reviews.map((review, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ delay: index * 0.1 }}
              viewport={{ once: true }}
              className="bg-white border-none rounded-[2rem] p-8 shadow-xl shadow-slate-200/50 flex flex-col relative group hover:-translate-y-2 transition-all duration-500"
            >
              <Quote className="absolute top-6 right-6 w-8 h-8 text-slate-50 opacity-10 group-hover:text-amber-500/20 transition-colors" />

              {/* Stars */}
              <div className="flex items-center gap-0.5 mb-4">
                {[...Array(review.rating)].map((_, i) => (
                  <Star
                    key={i}
                    className="w-3.5 h-3.5 fill-amber-500 text-amber-500"
                  />
                ))}
              </div>

              {/* Review text */}
              <p className="text-slate-600 text-sm leading-relaxed mb-6 italic font-medium">
                “{review.text}”
              </p>

              {/* User Info */}
              <div className="mt-auto pt-6 border-t border-slate-50 flex items-center justify-between">
                <div>
                  <p className="font-black text-slate-900 text-sm uppercase tracking-tight">
                    {review.name}
                  </p>
                  <p className="text-[10px] text-amber-600 font-bold uppercase tracking-widest">
                    {review.date}
                  </p>
                </div>
                <div className="w-8 h-8 bg-slate-50 rounded-full flex items-center justify-center">
                  <img
                    src={`https://ui-avatars.com/api/?name=${review.name}&background=random`}
                    alt="user"
                    className="w-full h-full rounded-full opacity-80"
                  />
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Google CTA */}
        <div className="text-center">
          <Button
            onClick={() => window.open(reviewsData.googleReviewUrl, "_blank")}
            className="bg-slate-900 hover:bg-amber-500 text-white hover:text-slate-900 rounded-2xl px-10 py-8 text-lg font-black transition-all shadow-2xl shadow-slate-900/20 group"
          >
            WRITE A REVIEW ON GOOGLE
            <ExternalLink className="w-5 h-5 ml-3 group-hover:rotate-12 transition-transform" />
          </Button>

          <div className="flex items-center justify-center gap-2 mt-6">
            <div className="h-px w-8 bg-slate-200" />
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.3em]">
              Verified Google Business Profile
            </p>
            <div className="h-px w-8 bg-slate-200" />
          </div>
        </div>
      </div>
    </section>
  );
};

export default ReviewsSection;
